/**
 * Enhanced Business Hours Types
 * Week 3: BusinessHours拡張実装
 * 
 * 高度な営業時間管理 - 特別営業日・複数時間帯・期間限定対応
 */

// =============================================================================
// 🕒 基本時間型
// =============================================================================

export interface TimeSlot {
  start: string;  // "10:00" format
  end: string;    // "20:00" format
}

export interface BreakTime {
  id: string;
  name: string;       // "昼休み", "清掃時間" etc.
  start: string;
  end: string;
  recurring: boolean; // 毎日同じ時間か
}

// =============================================================================
// 🗓️ 営業時間スケジュール
// =============================================================================

export interface DailySchedule {
  isOpen: boolean;
  
  // 複数の営業時間帯（昼と夜の営業など）
  timeSlots: TimeSlot[];
  
  // 休憩時間
  breaks: BreakTime[];
  
  // 特別設定
  isHoliday?: boolean;
  holidayReason?: string; // "定休日", "祝日", "臨時休業"
  
  // 容量制限
  maxCapacity?: number;
  staffRequired?: number;
  
  // 備考
  notes?: string;
}

export interface WeeklySchedule {
  monday: DailySchedule;
  tuesday: DailySchedule;
  wednesday: DailySchedule;
  thursday: DailySchedule;
  friday: DailySchedule;
  saturday: DailySchedule;
  sunday: DailySchedule;
}

// =============================================================================
// 🎯 特別営業日・例外設定
// =============================================================================

export interface SpecialBusinessDay {
  id: string;
  date: string;        // "2024-12-25" format
  type: 'special_hours' | 'closed' | 'extended_hours' | 'event';
  
  // 特別営業時間
  schedule?: DailySchedule;
  
  // 理由・説明
  reason: string;      // "クリスマス", "年末年始", "メンテナンス"
  description?: string;
  
  // 顧客向け表示
  customerMessage?: string;
  isPublicHoliday?: boolean;
  
  // 予約制限
  allowReservations: boolean;
  maxReservations?: number;
  
  // 作成・更新情報
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// =============================================================================
// 🔄 繰り返し例外パターン
// =============================================================================

export interface RecurringException {
  id: string;
  name: string;
  pattern: 'weekly' | 'monthly' | 'yearly' | 'custom';
  
  // 繰り返しルール
  recurrence: {
    frequency: number;  // 毎週、隔週など
    dayOfWeek?: number; // 0-6 (日-土)
    dayOfMonth?: number; // 1-31
    month?: number;      // 1-12
    weekOfMonth?: number; // 第何週
  };
  
  // 適用する変更
  override: DailySchedule;
  
  // 適用期間
  startDate: string;
  endDate?: string;
  
  // 除外日
  excludeDates: string[];
  
  isActive: boolean;
}

// =============================================================================
// 🌟 季節・期間限定営業時間
// =============================================================================

export interface SeasonalHours {
  id: string;
  name: string;        // "夏季営業", "年末年始", "春の特別営業"
  
  // 適用期間
  startDate: string;
  endDate: string;
  
  // 年次繰り返し
  isYearlyRecurring: boolean;
  
  // 特別スケジュール
  weeklySchedule: WeeklySchedule;
  
  // 優先度（数値が高いほど優先）
  priority: number;
  
  // 説明
  description?: string;
  customerMessage?: string;
  
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// 🎌 祝日管理
// =============================================================================

export interface Holiday {
  id: string;
  name: string;
  date: string;
  type: 'national' | 'regional' | 'store_specific';
  
  // 営業設定
  businessRule: 'closed' | 'special_hours' | 'normal_hours';
  specialSchedule?: DailySchedule;
  
  // 年次繰り返し
  isRecurring: boolean;
  recurringRule?: {
    type: 'fixed_date' | 'nth_weekday' | 'easter_based';
    month?: number;
    day?: number;
    weekOfMonth?: number;
    dayOfWeek?: number;
  };
  
  // 表示情報
  customerMessage?: string;
  isPublicHoliday: boolean;
}

// =============================================================================
// 📋 統合営業時間設定
// =============================================================================

export interface EnhancedBusinessHours {
  // 基本営業時間（デフォルト）
  defaultWeeklySchedule: WeeklySchedule;
  
  // 特別営業日
  specialDays: SpecialBusinessDay[];
  
  // 繰り返し例外
  recurringExceptions: RecurringException[];
  
  // 季節営業時間
  seasonalHours: SeasonalHours[];
  
  // 祝日設定
  holidays: Holiday[];
  
  // タイムゾーン設定
  timezone: string;
  
  // 予約可能時間制限
  reservationRules: {
    advanceBookingDays: number;      // 何日前まで予約可能
    sameDayBookingCutoff: string;    // 当日予約の締切時間
    preventPastBooking: boolean;
    minServiceDuration: number;      // 最小サービス時間（分）
    maxServiceDuration: number;      // 最大サービス時間（分）
    bufferBetweenServices: number;   // サービス間のバッファ時間（分）
  };
  
  // 自動化設定
  automation: {
    autoCloseOnHolidays: boolean;
    syncWithGoogleCalendar: boolean;
    notifyStaffOfChanges: boolean;
    updateWebsite: boolean;
  };
  
  // メタデータ
  lastUpdated: Date;
  updatedBy: string;
  version: string;
}

// =============================================================================
// 🔍 営業時間解決ヘルパー
// =============================================================================

export interface ResolvedSchedule {
  date: string;
  schedule: DailySchedule;
  source: 'default' | 'special_day' | 'recurring_exception' | 'seasonal' | 'holiday';
  reason?: string;
}

export interface BusinessHoursValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// =============================================================================
// 📊 営業時間統計
// =============================================================================

export interface BusinessHoursStats {
  totalOperatingHours: number;
  averageHoursPerDay: number;
  mostBusyDay: string;
  leastBusyDay: string;
  totalHolidays: number;
  specialDaysCount: number;
  
  // 月別統計
  monthlyStats: {
    month: string;
    operatingDays: number;
    totalHours: number;
    holidaysCount: number;
  }[];
}

// =============================================================================
// 🛠️ デフォルト設定
// =============================================================================

export const DEFAULT_DAILY_SCHEDULE: DailySchedule = {
  isOpen: true,
  timeSlots: [
    { start: '10:00', end: '20:00' }
  ],
  breaks: [],
  maxCapacity: 4,
  staffRequired: 1,
};

export const DEFAULT_WEEKLY_SCHEDULE: WeeklySchedule = {
  monday: { ...DEFAULT_DAILY_SCHEDULE, isOpen: false, isHoliday: true, holidayReason: '定休日' },
  tuesday: DEFAULT_DAILY_SCHEDULE,
  wednesday: DEFAULT_DAILY_SCHEDULE,
  thursday: DEFAULT_DAILY_SCHEDULE,
  friday: DEFAULT_DAILY_SCHEDULE,
  saturday: DEFAULT_DAILY_SCHEDULE,
  sunday: DEFAULT_DAILY_SCHEDULE,
};

export const DEFAULT_ENHANCED_BUSINESS_HOURS: EnhancedBusinessHours = {
  defaultWeeklySchedule: DEFAULT_WEEKLY_SCHEDULE,
  specialDays: [],
  recurringExceptions: [],
  seasonalHours: [],
  holidays: [],
  timezone: 'Asia/Tokyo',
  reservationRules: {
    advanceBookingDays: 30,
    sameDayBookingCutoff: '16:00',
    preventPastBooking: true,
    minServiceDuration: 30,
    maxServiceDuration: 180,
    bufferBetweenServices: 15,
  },
  automation: {
    autoCloseOnHolidays: true,
    syncWithGoogleCalendar: false,
    notifyStaffOfChanges: true,
    updateWebsite: false,
  },
  lastUpdated: new Date(),
  updatedBy: 'system',
  version: '1.0.0',
};